import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { TariffUsageItem } from '../../../apis/types'
import { getTariffUsagesApi } from '../../../apis/apis'
import Panel from '../../../components/Panel'
import Table from '../../../components/Table/Table'
import QueryError from '../../conductor/QueryError'

type UsageRow = TariffUsageItem & { id: string }

/** Rows shown before expanding on small lists. */
const PREVIEW_LIMIT = 5
/** Max rows we load or expand inline; larger lists are sample-only. */
export const INLINE_TABLE_CAP = 25

const UsagesLoadingIndicator: React.FC = () => (
    <div
        className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300"
        aria-busy="true"
        aria-live="polite"
    >
        <svg
            className="h-5 w-5 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
        >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
        </svg>
        Loading...
    </div>
)

function fetchParams(isLargeList: boolean): { activeOnly: boolean; resultsPerPage: number } {
    if (isLargeList) {
        return { activeOnly: true, resultsPerPage: PREVIEW_LIMIT }
    }
    return { activeOnly: false, resultsPerPage: INLINE_TABLE_CAP }
}

export const TariffUsagesPanel: React.FC<{
    tariffCuid: string
    activeUsageCount: number
    expanded?: boolean
    onExpandedChange?: (expanded: boolean) => void
    /** Table only, for the blocked-delete modal. */
    embedded?: boolean
}> = ({ tariffCuid, activeUsageCount, expanded = false, onExpandedChange, embedded = false }) => {
    const [rows, setRows] = useState<UsageRow[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [showInactive, setShowInactive] = useState(false)
    const [showAll, setShowAll] = useState(false)

    const isLargeList = activeUsageCount > INLINE_TABLE_CAP

    const load = useCallback((): void => {
        setLoading(true)
        setError(false)
        const { activeOnly, resultsPerPage } = fetchParams(isLargeList)

        void getTariffUsagesApi(tariffCuid, { page: 0, resultsPerPage, activeOnly })
            .then((data) => {
                setRows(
                    data.results.map((u) => ({
                        ...u,
                        id: u.meterServiceCuid,
                    }))
                )
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false))
    }, [isLargeList, tariffCuid])

    useEffect(() => {
        setShowAll(false)
        setShowInactive(false)
    }, [tariffCuid])

    const isExpanded = embedded || expanded

    useEffect(() => {
        if (!isExpanded) return
        load()
    }, [isExpanded, load])

    const filteredRows = useMemo(
        () => (showInactive ? rows : rows.filter((r) => r.isActive)),
        [rows, showInactive]
    )
    const hasMoreThanPreview = !isLargeList && filteredRows.length > PREVIEW_LIMIT
    const visibleRows =
        isLargeList || showAll || !hasMoreThanPreview
            ? filteredRows
            : filteredRows.slice(0, PREVIEW_LIMIT)
    const badgeCount = showInactive ? rows.length : activeUsageCount
    const inactiveHiddenCount = showInactive ? 0 : rows.filter((r) => !r.isActive).length
    const remainingLargeListUsages = isLargeList
        ? Math.max(activeUsageCount - visibleRows.length, 0)
        : 0

    const tableColumns = useMemo(
        () => [
            {
                label: 'Tenant',
                render: (rec: UsageRow) => (
                    <Link
                        to={
                            rec.tenantAccountNumber != null
                                ? `/tenant/tenants/${rec.tenantCuid}?serviceAccountNumber=${encodeURIComponent(rec.tenantAccountNumber)}`
                                : `/tenant/tenants/${rec.tenantCuid}`
                        }
                        className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                    >
                        {rec.tenantName ?? rec.tenantCuid}
                    </Link>
                ),
            },
            {
                label: 'OneBill™',
                render: (rec: UsageRow) => rec.tenantAccountNumber ?? null,
            },
            {
                label: 'Utility',
                render: (rec: UsageRow) => rec.utilityAccountNumber ?? null,
            },
            {
                label: 'Status',
                render: (rec: UsageRow) =>
                    rec.isActive ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-200">
                            Active
                        </span>
                    ) : (
                        <span className="inline-flex items-center rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                            Inactive
                        </span>
                    ),
            },
        ],
        []
    )

    const inactiveToggle =
        !isLargeList && rows.length > 0 ? (
            <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-300">
                <input
                    type="checkbox"
                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800"
                    checked={showInactive}
                    onChange={(e) => {
                        setShowInactive(e.target.checked)
                        setShowAll(false)
                    }}
                />
                Show inactive
            </label>
        ) : null

    const tableBody =
        error ? (
            <QueryError message="Failed to load tariff usages." onRetry={load} />
        ) : loading ? (
            <UsagesLoadingIndicator />
        ) : visibleRows.length === 0 ? (
            <p className="py-2 text-sm text-slate-500 dark:text-slate-400">
                {inactiveHiddenCount > 0
                    ? 'No active meter service usages.'
                    : 'This tariff has no meter service usages.'}
            </p>
        ) : (
            <>
                <Table<UsageRow>
                    compact
                    hidePaginator
                    highlightRowsOnHover
                    handlePaginatorClick={() => {}}
                    data={{
                        results: visibleRows,
                        total: visibleRows.length,
                        page: 0,
                        resultsPerPage: Math.max(visibleRows.length, 1),
                    }}
                    colConfig={tableColumns}
                />
                {remainingLargeListUsages > 0 ? (
                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                        …and {remainingLargeListUsages.toLocaleString()} more usages
                    </p>
                ) : null}
                {hasMoreThanPreview ? (
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                        {!showAll ? (
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                Showing {PREVIEW_LIMIT} of {filteredRows.length.toLocaleString()}
                                {showInactive ? '' : ' active'} meter services.
                            </p>
                        ) : null}
                        <button
                            type="button"
                            onClick={() => setShowAll((prev) => !prev)}
                            className="text-xs font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                        >
                            {showAll
                                ? 'Show less'
                                : `View all ${filteredRows.length.toLocaleString()}`}
                        </button>
                    </div>
                ) : null}
            </>
        )

    if (embedded) {
        return (
            <div>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Meter service usages
                        {!showInactive && inactiveHiddenCount > 0
                            ? ` (${inactiveHiddenCount} inactive hidden)`
                            : ''}
                    </span>
                    {inactiveToggle}
                </div>
                {tableBody}
            </div>
        )
    }

    return (
        <div>
            {!isExpanded ? (
                <button
                    type="button"
                    onClick={() => onExpandedChange?.(true)}
                    className="mb-5 flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 text-left text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700/50"
                >
                    <span className="inline-flex items-center gap-2">
                        Show meter service usages
                        <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-indigo-600 px-1.5 text-xs font-semibold tabular-nums text-white dark:bg-indigo-500">
                            {activeUsageCount}
                        </span>
                    </span>
                </button>
            ) : (
            <Panel
                header={
                    <span className="flex w-full flex-wrap items-center justify-between gap-2">
                        <span className="inline-flex items-center gap-2">
                            Meter service usages
                            <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-indigo-600 px-1.5 text-xs font-semibold tabular-nums text-white dark:bg-indigo-500">
                                {badgeCount}
                            </span>
                            {!showInactive && inactiveHiddenCount > 0 ? (
                                <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
                                    ({inactiveHiddenCount} inactive hidden)
                                </span>
                            ) : null}
                        </span>
                        {inactiveToggle}
                    </span>
                }
                className="mb-5"
            >
                {tableBody}
            </Panel>
            )}
        </div>
    )
}

export default TariffUsagesPanel
